// Position type is used in function signatures

/**
 * Convert degrees to radians
 */
export function toRad(degrees: number): number {
  return (degrees * Math.PI) / 180
}

/**
 * Convert radians to degrees
 */
export function toDeg(radians: number): number {
  return (radians * 180) / Math.PI
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in meters
 */
export function haversineMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000 // Earth's radius in meters
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Clamp angle delta to [-180, 180] range
 */
export function clampAngleDeltaDeg(delta: number): number {
  let d = ((delta + 180) % 360) - 180
  if (d < -180) d += 360
  return d
}

/**
 * Calculate bearing from point 1 to point 2 in degrees
 * Returns bearing in range [0, 360)
 */
export function bearingDeg(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const φ1 = toRad(lat1)
  const φ2 = toRad(lat2)
  const λ1 = toRad(lon1)
  const λ2 = toRad(lon2)
  const y = Math.sin(λ2 - λ1) * Math.cos(φ2)
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(λ2 - λ1)
  let θ = Math.atan2(y, x)
  return (toDeg(θ) + 360) % 360
}

/**
 * Get current timestamp in milliseconds
 */
export function now(): number {
  return Date.now()
}

/**
 * Get geo options for geolocation API
 */
export function geoOptions(): PositionOptions {
  return {
    enableHighAccuracy: false,
    maximumAge: 0,
    timeout: 15000,
  }
}
